import { createAsyncThunk, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { CookieHelper } from '../Helpers/CookieHelper.ts';
import { RootState } from '../Store/store.ts';
import { getAdditionalFilterForOptions, SetFitStatusForAllProducts } from '../../FitAssistantShared/Functions.js';
import { getSearchParametersFromQuery, setQueryFromSearchParameters } from '../Util/QueryFromParameters.ts';
import { FitAssistantOptionsResponse } from '../Types/FitAssistantGraphQLTypes.ts';
import { CarInformation, InitialData, Step, StepData } from '../Types/FitAssistantData.ts';
import { getPerCategorySearchString, getSearchString } from '../Util/SearchString.ts';
import { GET_PRODUCT_QUERY } from '../Queries/Queries.ts';
import { getWindowData } from '../../FitAssistantShared/GetWindowData.js';
import { client } from '/Foundation/SitecoreGraphQL/code/clientv2.js';
import { GetProductsQuery } from '../Types/FitAssistantGraphQLTypes.ts';
import {
  addSelectedCarFromCookie,
  setFitStatus,
  setShowFitAssistant,
  setStepEnum,
} from '../Slices/FitAssistantSlice.ts';
import { ProductLoader } from '../Helpers/ProductLoader.ts';
import { IsProductPage } from '../../FitAssistantShared/IsProductPage.js';
import { BodyData, RejectValue, SelectedCarFromCookie } from './Types.ts';
import { resetFitAssistant } from './Reset.ts';
import FitAssistantStep from '../Enums/FitAssistantStep.ts';
import FitAssistantVariation from '../Enums/FitAssistantVariation.ts';
import FitAssistantStatus from '../Enums/FitAssistantStatus.ts';
import { ProductPageHelper } from '../Helpers/ProductPageHelper.ts';
import { selectSolution } from '../Util/SelectSolution.ts';

export const loadOptionsFromCookie = createAsyncThunk<string, void, RejectValue>(
  'fitAssistant/loadOptionsFromCookie',
  async (_, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
    let state = getState() as RootState;
    let cookieData = await GetCookieData(state.FitAssistant.initialData, state, dispatch);

    if (cookieData.length === 0 || cookieData === null) {
      return fulfillWithValue('No cookie or query string set.');
    }

    const error = await applyLoadedCarOptions(cookieData, { isNewSelection: false }, state, dispatch);
    if (error) return rejectWithValue(error);

    return fulfillWithValue('Loading options from cookie or query string finished');
  }
);

type LoadedCarSettings = {
  isNewSelection: boolean;
  preferredSolutionId?: string | null;
};

export async function applyLoadedCarOptions(
  cookieData: FitAssistantOptionsResponse[],
  { isNewSelection, preferredSolutionId }: LoadedCarSettings,
  state: RootState,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
): Promise<string | undefined> {
  let initialData = state.FitAssistant.initialData;

  const getArrayOrder = initialData.Steps.map((step) => step.Parameter);

  const sortArrayByListOrder = cookieData.sort(
    (a: FitAssistantOptionsResponse, b: FitAssistantOptionsResponse) =>
      getArrayOrder.indexOf(a.ParameterName) - getArrayOrder.indexOf(b.ParameterName)
  );

  const sliderList: StepData[] = sortArrayByListOrder.map((item) => ({
    parameters: item.ParameterName,
    configItems: item.NextConfigurationItem,
    data: item.Options,
    messages: item.OptionMessages,
  }));

  let selectedCar: CarInformation[] = GetSelectedCar(sortArrayByListOrder);

  let selectedCarError = GetCarErrors(initialData, sliderList, selectedCar);

  const searchString = getSearchString(selectedCar);

  const perCategorySearchString = getPerCategorySearchString(selectedCar);

  let noValueParameters = selectedCar.filter((x) => x.value === '0').map((y) => y.parameterName);
  let filteredSteps = initialData.Steps.filter(
    (x) => !x.SkipWhenLoadingFromCookie && !noValueParameters.includes(x.Parameter)
  );

  let currentStep: Step | undefined = filteredSteps.length > 0 ? filteredSteps[filteredSteps.length - 1] : undefined;

  if (!currentStep) return 'The initial data contained no step content.';

  const selectedCarFromCookie: SelectedCarFromCookie = {
    getSliderList: sliderList,
    getSelectedCar: selectedCar,
  };

  if (selectedCarError.length === 0) {
    const getOptionsVariables: Record<string, string | boolean | null> = {
      siteName: getWindowData().SitecoreSiteName,
      language: getWindowData().SitecoreLanguage,
      configurationItem: currentStep?.Id,
      selectedproductpimid: state.ProductDetail.ProductId || '',
      parameters: `${searchString},${perCategorySearchString}`,
      additionalFilter: getAdditionalFilterForOptions(currentStep, state.FitAssistant.initialData.AdditionalFilters),
      ispreview: state.FitAssistant.initialData.IsPreview,
    };

    let result = await client.query<GetProductsQuery>(GET_PRODUCT_QUERY, getOptionsVariables);

    if (result.error) return result.error;

    if (state.FitAssistant.initialData.Steps && state.FitAssistant.initialData.Steps.length > 0) {
      dispatch(setStepEnum(state.FitAssistant.initialData.Steps[0].Parameter));
    }

    if (selectedCarFromCookie && cookieData.length > 0 && result.data && selectedCarError?.length === 0) {
      dispatch(addSelectedCarFromCookie(selectedCarFromCookie));

      let productLoader = new ProductLoader(
        result.data,
        selectedCarFromCookie.getSelectedCar,
        selectedCarFromCookie,
        isNewSelection,
        state.FitAssistant.initialData,
        selectedCarFromCookie.getSliderList,
        isNewSelection,
        dispatch
      );

      productLoader.Load();

      if (preferredSolutionId) selectPreferredSolution(result.data, preferredSolutionId, dispatch);

      if (IsProductPage)
        setQueryFromSearchParameters(initialData.FitAssistantCookieName, selectedCarFromCookie.getSelectedCar);

      let invalidCar =
        sortArrayByListOrder.filter(
          (x) => ['make', 'model', 'year'].indexOf(x.ParameterName) > -1 && x.Options?.find((option) => option.Selected)
        )?.length < 3;

      if (invalidCar) {
        dispatch(resetFitAssistant(false));
      }
    } else {
      const answeredSteps = selectedCar.filter((x) => x.value !== '0');
      const lastAnsweredStep =
        answeredSteps.length > 0
          ? answeredSteps[answeredSteps.length - 1].parameterName
          : initialData.Steps[0].Parameter;

      dispatch(addSelectedCarFromCookie({ ...selectedCarFromCookie, step: lastAnsweredStep }));
      dispatch(setShowFitAssistant(false));
    }
  } else {
    dispatch(addSelectedCarFromCookie({ ...selectedCarFromCookie, step: selectedCarError[0] }));
    dispatch(setShowFitAssistant(false));
  }
  return undefined;
}

function selectPreferredSolution(
  data: GetProductsQuery,
  solutionId: string,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
) {
  const response = data.getCarSelectorProducts?.carSelectorProductsResponse;
  if (!IsProductPage || !response?.ProductPageMatch) return;

  const index = response.MatchingProducts?.findIndex((x) => x.SolutionId === solutionId) ?? -1;
  if (index <= 0) return;

  selectSolution(
    index,
    {
      solutionComponents: response.SolutionComponents,
      matchingProducts: response.MatchingProducts,
      solutionPriceContainer: response.SolutionPriceContainer,
      addToCartButton: response.AddToCartButton,
    },
    dispatch
  );
}

async function GetCookieData(
  initialData: InitialData,
  state: RootState,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
): Promise<FitAssistantOptionsResponse[]> {
  let cookies = CookieHelper.GetCookies();
  const getSelectedCarFromQuery = getSearchParametersFromQuery(initialData.FitAssistantCookieName);

  if (!getSelectedCarFromQuery && !cookies.cookie) {
    if (cookies.perCategoryCookie === FitAssistantStatus.RackMounting) {
      dispatch(setFitStatus(FitAssistantStatus.RackMounting));

      if (IsProductPage) {
        ProductPageHelper.HandleRackMountingProductPageData(state.ProductDetail, initialData, dispatch);
      } else {
        SetFitStatusForAllProducts(FitAssistantStatus.Confirmed);
      }
    }

    return [];
  }

  const bodydata = GetBodyData(initialData);

  let cookieData: FitAssistantOptionsResponse[] = await fetch(
    '/api/sitecore/fitguide/LoadOptionsFromQueryStringOrCookie',
    {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(bodydata),
    }
  )
    .then((response) => response.json())
    .catch();

  return cookieData;
}

function GetBodyData(initialData: InitialData): BodyData {
  let cookies = CookieHelper.GetCookies();
  const getSelectedCarFromQuery = getSearchParametersFromQuery(initialData.FitAssistantCookieName);

  let bodyData: BodyData = {
    selectedCarFromCookieOrQuery: getSelectedCarFromQuery ?? `${cookies.cookie},${cookies.perCategoryCookie}`,
    steps: initialData.Steps.filter((x) => !x.SkipWhenLoadingFromCookie),
    additionalFilter: initialData.AdditionalFilters ? JSON.stringify(initialData.AdditionalFilters) : null,
  };
  return bodyData;
}

function GetSelectedCar(optionsResponse: FitAssistantOptionsResponse[]): CarInformation[] {
  let selectedCar: CarInformation[] = optionsResponse
    .map((item) => {
      let selectedItem = item.Options?.find((selectedItem) => selectedItem.Selected);
      return { item, selectedItem };
    })
    .map(({ item, selectedItem }) => {
      return {
        parameterName: item?.ParameterName,
        configurationItem: item.NextConfigurationItem,
        text: selectedItem && selectedItem.Text ? selectedItem.Text : 'no-value',
        value:
          selectedItem && selectedItem?.BedTypeLovId
            ? selectedItem.BedTypeLovId
            : selectedItem?.Value
              ? selectedItem.Value
              : '0',
      };
    });

  return selectedCar;
}

function GetCarErrors(initialData: InitialData, getSliderList: StepData[], selectedCar: CarInformation[]) {
  let errors = getSliderList
    .map((item) => {
      let selectedItem = selectedCar.find((findElement) => findElement.parameterName === item.parameters);
      return { getSliderListArray: item, getSelectedCarArray: selectedItem };
    })
    .filter(
      ({ getSliderListArray, getSelectedCarArray }) =>
        getSliderListArray.data?.length > 0 && getSelectedCarArray?.text === 'no-value'
    )
    .map(({ getSliderListArray }) => getSliderListArray.parameters);

  if (
    initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents ||
    initialData.FitAssistantVariation === FitAssistantVariation.RoofPlatforms
  ) {
    return errors.filter((x) => x !== FitAssistantStep.RoofRacks);
  }

  return errors;
}
